// Classifies a failed team-invite accept/decline, so the accept-team landing page and the Teams >
// Invitations tab treat the same backend error the same way.

// gloss-back's stable codes ("ErrorCode", gloss-back#98) for the invite failures below. Matched
// first; the English messages after them are the fallback for a backend that doesn't send codes
// yet, and can go once every deployed backend does.
const INVITE_ERROR_CODES = new Map<string, InviteFailure>([
  ["invite_not_pending", { kind: "noLongerPending", key: "inviteNoLongerPending" }],
  ["invite_link_superseded", { kind: "superseded", key: "inviteLinkSuperseded" }],
  ["invite_link_expired", { kind: "superseded", key: "inviteLinkExpired" }],
]);

// gloss-back's errors for an invite that's no longer there to act on: cancelled or revoked by
// the owner, or already accepted.
const NO_LONGER_PENDING_ERRORS = [
  "Invitation not found",
  "Error accepting the invitation",
  "User already accepted the invitation",
];

// gloss-back's error for a link from an earlier email, after the invite was resent.
const LINK_SUPERSEDED_ERROR = "This invitation link has been replaced by a newer one";

// gloss-back's 400 (decode_jwt) for an email link opened after invitation_expiration_hours
// (default 120h), on accept and on a team invite's decline (#6258). The invite itself is still
// pending, and Teams > Invitations mints a fresh token on every load, so like a superseded link
// the user can still act on it there - no admin needed.
const LINK_EXPIRED_ERROR = "Token has expired";

// gloss-back's 400 from /connect/token when ApiService's token refresh fails (revoked or expired
// refresh token): it's about the session, not the invite, so it mustn't block Accept / Decline.
const SESSION_EXPIRED_ERROR = "Invalid refresh token";

// 4xx statuses a retry can still fix: a timeout or rate limiting. Everything outside 4xx (no
// response at all, which ApiService rejects with statusCode 0, or a 5xx) is retryable too.
const RETRYABLE_4XX = new Set([408, 429]);

export type InviteFailureKind =
  // The invite was cancelled, revoked or already accepted.
  | "noLongerPending"
  // A newer invite email replaced this link's token. (Also an expired link: the invite is still
  // pending, with a fresh token in Teams > Invitations.)
  | "superseded"
  // Network error, timeout, rate limit or 5xx: trying again may work.
  | "retryable"
  // Any other 4xx (2FA required, wrong account, ...): retrying can't succeed.
  | "permanent";

export interface InviteFailure {
  kind: InviteFailureKind;
  // i18n key for the message to show.
  key: string;
}

const FAILURE_KEYS = {
  accept: { retryable: "inviteAcceptFailed", permanent: "inviteAcceptFailedPermanent" },
  decline: { retryable: "inviteDeclineFailed", permanent: "inviteDeclineFailedPermanent" },
} as const;

export function classifyInviteFailure(e: unknown, action: "accept" | "decline"): InviteFailure {
  const { retryable: retryableKey, permanent: permanentKey } = FAILURE_KEYS[action];
  const errorCode = (e as { errorCode?: unknown })?.errorCode;
  const coded = typeof errorCode === "string" ? INVITE_ERROR_CODES.get(errorCode) : undefined;
  if (coded) {
    return { ...coded };
  }
  const message = inviteErrorMessage(e);
  if (message.includes(LINK_SUPERSEDED_ERROR)) {
    return { kind: "superseded", key: "inviteLinkSuperseded" };
  }
  if (message.includes(LINK_EXPIRED_ERROR)) {
    return { kind: "superseded", key: "inviteLinkExpired" };
  }
  if (message.includes(SESSION_EXPIRED_ERROR)) {
    return { kind: "retryable", key: "loginExpired" };
  }
  if (NO_LONGER_PENDING_ERRORS.some((m) => message.includes(m))) {
    return { kind: "noLongerPending", key: "inviteNoLongerPending" };
  }
  const status = (e as { statusCode?: unknown })?.statusCode;
  const isPermanent =
    typeof status === "number" && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status);
  return isPermanent
    ? { kind: "permanent", key: permanentKey }
    : { kind: "retryable", key: retryableKey };
}

// ApiService rejects with an ErrorResponse, which isn't an Error, so `String(e)` would log
// "[object Object]". Prefer its message; an ErrorResponse without one (e.g. a 5xx with an empty
// or non-JSON body) falls back to its status; anything else to the string form.
export function inviteErrorMessage(e: unknown): string {
  const { message, statusCode } = (e ?? {}) as { message?: unknown; statusCode?: unknown };
  if (typeof message === "string" && message) {
    return message;
  }
  if (typeof statusCode === "number") {
    return `Request failed with status ${statusCode}`;
  }
  return String(e);
}
